/**
 * Airtable ↔ FlexDate adapter.
 *
 * Arcos stores dates in memory as the precision-aware {@link FlexDate} primitive.
 * Persistence to Airtable, however, only supports two field shapes:
 *   - Date (no time)  → "YYYY-MM-DD"
 *   - Date with time  → ISO 8601 with timezone (e.g. "2026-04-27T14:30:00.000Z")
 *
 * Year-less (annual / birthday), year-month, and year-only FlexDates lose
 * precision when written to a standard Airtable date column. Per the Calendar
 * View plan's v1 (a) recommendation, the adapter refuses such writes and
 * surfaces a specific error so the UI layer can display it.
 *
 * The adapter is invoked at the persistence boundary — the `updateItemFields`
 * (and `createItem`) call site in `src/data/AirtableDataService.js`. The
 * reducer never calls these functions; in-memory state stays as FlexDate.
 *
 * Future Supabase adapter (`supabaseRowToFlexDate` / `flexDateToSupabaseRow`)
 * replaces this file with no Calendar / FlexDate / field-constraint changes.
 */

import { isValid, precision } from './flexDate.js';
import { instantFromWall, wallInZone } from './spaceTimeZone.js';

/**
 * Field-type constants. Mirrors the subset of Airtable field types we care
 * about for date persistence; the Airtable API uses the same string ids.
 */
export const AIRTABLE_FIELD_TYPES = Object.freeze({
  DATE: 'date',
  DATETIME: 'dateTime',
});

/**
 * Error thrown when the adapter refuses a write because the FlexDate's
 * precision can't be represented by the destination Airtable field. The UI
 * layer should catch this and surface `error.message` to the user.
 */
export class FlexDateWriteError extends Error {
  constructor(message, { fd, fieldType, reason } = {}) {
    super(message);
    this.name = 'FlexDateWriteError';
    this.fd = fd;
    this.fieldType = fieldType;
    this.reason = reason;
  }
}

function pad2(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

function ymd(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Convert an Airtable date / datetime field value into a FlexDate, where the
 * datetime fields represent **local wall-clock time**.
 *
 * Airtable stores datetime values as UTC ISO strings (e.g. "2026-04-28T17:22Z"
 * for a 1:22 PM EDT entry). To match user expectations across the app — and
 * since flexDate.format() / toDate() interpret the FlexDate's hour/minute
 * fields as local — we convert UTC → local on read. The conversion uses the
 * environment's local timezone (via JS's native Date object), so the same
 * record renders correctly for users in any zone.
 *
 * Read-direction mapping:
 *   "YYYY-MM-DD"                       → date-only FlexDate (no time, no tz)
 *   "YYYY-MM-DDTHH:MM:SS[.sss]Z"       → datetime FlexDate; fields are LOCAL
 *   "YYYY-MM-DDTHH:MM:SS[.sss]±HH:MM"  → datetime FlexDate; fields are LOCAL
 *   null / "" / undefined              → null
 *
 * No `tz` field is set on the returned FlexDate — fields are treated as naive
 * local. flexDateToAirtableValue() inverts this on write (local → UTC ISO).
 *
 * `fieldType` is advisory; `value`'s shape is the source of truth.
 *
 * `zone` (an IANA name) reads the fields on that zone's clock instead of the
 * environment's — the calendar passes the space's zone so an event is filed
 * under the day and hour where it happens. flexDateToAirtableValue takes the
 * same option, and a caller must pass it to both or neither.
 *
 * @param {string|null|undefined} value
 * @param {string} [fieldType]
 * @param {{ zone?: string|null }} [opts]
 * @returns {import('./flexDate.js').FlexDate|null}
 */
export function airtableValueToFlexDate(value, fieldType, { zone = null } = {}) {
  if (value == null || value === '') return null;
  if (typeof value !== 'string') return null;

  // Date-only: "YYYY-MM-DD" — no time component, no zone math needed.
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateMatch) {
    const year  = Number(dateMatch[1]);
    const month = Number(dateMatch[2]);
    const day   = Number(dateMatch[3]);
    const fd = { year, month, day };
    return isValid(fd) ? fd : null;
  }

  // Datetime: parse via JS Date (handles Z and ±HH:MM uniformly), then read
  // local fields. This produces wall-clock-in-local-time fields regardless of
  // the input's original timezone.
  const dtRe = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?$/;
  if (!dtRe.test(value)) return null;

  const d = new Date(value);
  if (isNaN(d.getTime())) return null;

  if (zone) {
    const w = wallInZone(d, zone);
    return w && isValid(w) ? w : null;
  }
  const fd = {
    year:   d.getFullYear(),
    month:  d.getMonth() + 1,
    day:    d.getDate(),
    hour:   d.getHours(),
    minute: d.getMinutes(),
  };
  return isValid(fd) ? fd : null;
}

/**
 * Convert a FlexDate to the Airtable wire format.
 *
 * Write-direction mapping:
 *   date-only FlexDate → "YYYY-MM-DD"
 *   datetime FlexDate  → ISO 8601 with timezone (UTC when tz is 'UTC' or absent
 *                        for a simple datetime; preserves explicit offset
 *                        strings; refuses to invent an offset for IANA names
 *                        because Airtable round-trips offsets, not zones)
 *
 *   year-only / year-month / annual → throws {@link FlexDateWriteError} per
 *   the v1 (a) recommendation. The UI must catch and display the error.
 *
 * @param {import('./flexDate.js').FlexDate} fd
 * @param {string} [fieldType]  Optional Airtable field type id ('date' or 'dateTime').
 * @param {{ zone?: string|null }} [opts]  read a tz-less datetime's fields on this zone's clock
 *   (see airtableValueToFlexDate).
 * @returns {string}
 * @throws {FlexDateWriteError} when `fd` has insufficient precision.
 */
export function flexDateToAirtableValue(fd, fieldType, { zone = null } = {}) {
  if (!isValid(fd)) {
    throw new FlexDateWriteError(
      'flexDateToAirtableValue: input is not a valid FlexDate.',
      { fd, fieldType, reason: 'invalid' },
    );
  }

  const p = precision(fd);

  if (p === 'year') {
    throw new FlexDateWriteError(
      'Cannot save a year-only date to Airtable: standard date fields require a full date (year, month, day). Add a month and day, or use a text field for partial dates.',
      { fd, fieldType, reason: 'partial-precision-year' },
    );
  }

  if (p === 'month') {
    throw new FlexDateWriteError(
      'Cannot save a year-month date to Airtable: standard date fields require a full date (year, month, day). Add a day, or use a text field for partial dates.',
      { fd, fieldType, reason: 'partial-precision-month' },
    );
  }

  if (p === 'annual') {
    throw new FlexDateWriteError(
      'Cannot save a year-less (annual / birthday) date to Airtable: standard date fields require a year. Add a year, or use a text field to store recurring annual dates.',
      { fd, fieldType, reason: 'annual' },
    );
  }

  if (p === 'day') {
    return ymd(fd.year, fd.month, fd.day);
  }

  // p === 'minute' — datetime. FlexDate fields are naive local wall-clock
  // (the convention airtableValueToFlexDate established on read). To produce
  // the UTC ISO string Airtable expects, build a JS Date with those fields
  // interpreted as local, then `.toISOString()` does the local→UTC conversion.
  // Tz field is honored only for legacy/explicit cases:
  //   - 'UTC' → fields are taken as literal UTC (preserves the historical
  //     "Z output, fields not converted" behavior; useful when the FlexDate
  //     was constructed manually for a known UTC instant)
  //   - '+HH:MM' / '-HH:MM' offset → fields are at that offset, output preserved
  //   - IANA name → refuse, Airtable can't round-trip zone names
  //   - absent (default) → naive local, convert to UTC
  const tz = fd.tz;
  if (tz === 'UTC') {
    const datePart = ymd(fd.year, fd.month, fd.day);
    const timePart = `${pad2(fd.hour)}:${pad2(fd.minute)}:00.000`;
    return `${datePart}T${timePart}Z`;
  }
  if (tz && /^[+-]\d{2}:?\d{2}$/.test(tz)) {
    const datePart = ymd(fd.year, fd.month, fd.day);
    const timePart = `${pad2(fd.hour)}:${pad2(fd.minute)}:00.000`;
    const normalized = tz.length === 5 ? `${tz.slice(0, 3)}:${tz.slice(3)}` : tz;
    return `${datePart}T${timePart}${normalized}`;
  }
  if (tz) {
    throw new FlexDateWriteError(
      `Cannot serialize FlexDate to Airtable: tz "${tz}" is an IANA zone name, but Airtable stores fixed offsets. Convert to a "+HH:MM" / "-HH:MM" offset before writing, or store as UTC.`,
      { fd, fieldType, reason: 'iana-tz' },
    );
  }
  if (zone) {
    const instant = instantFromWall(fd, zone);
    if (instant && !isNaN(instant.getTime())) return instant.toISOString();
  }
  // Default: naive local → UTC via JS Date. Local-to-UTC conversion uses
  // the environment's offset, which is what we want for the read↔write
  // round-trip established by airtableValueToFlexDate.
  const localDate = new Date(fd.year, fd.month - 1, fd.day, fd.hour, fd.minute, 0, 0);
  if (isNaN(localDate.getTime())) {
    throw new FlexDateWriteError(
      'flexDateToAirtableValue: failed to construct local Date from FlexDate fields.',
      { fd, fieldType, reason: 'invalid-local-date' },
    );
  }
  return localDate.toISOString();
}

/**
 * Validate a FlexDate against a field-constraint metadata object.
 *
 * Constraints (all optional, default false / true as noted):
 *   - `requireYear`   (default true)  — reject annuals when true
 *   - `requireTime`   (default false) — require precision === 'minute' when true
 *   - `allowYearless` (default false) — explicitly permits annuals; if true,
 *                                       it overrides `requireYear: true`
 *
 * Returns `{ valid: true }` on success, `{ valid: false, error: string }`
 * with a UI-ready message on failure.
 *
 * @param {import('./flexDate.js').FlexDate} fd
 * @param {{ requireYear?: boolean, requireTime?: boolean, allowYearless?: boolean }} [constraint]
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateFieldConstraint(fd, constraint = {}) {
  if (!isValid(fd)) {
    return { valid: false, error: 'Value is not a valid FlexDate.' };
  }

  const {
    requireYear   = true,
    requireTime   = false,
    allowYearless = false,
  } = constraint;

  const p = precision(fd);
  const isYearless = p === 'annual';

  if (isYearless && !allowYearless) {
    return {
      valid: false,
      error: 'This field requires a year (year-less / annual dates are not allowed here).',
    };
  }

  if (!isYearless && requireYear === false) {
    // No-op: dated values are always allowed when requireYear is false.
  }

  if (requireTime && p !== 'minute') {
    return {
      valid: false,
      error: 'This field requires a time (hour and minute).',
    };
  }

  return { valid: true };
}
